import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { handleResult } from 'modules/core/lib/handleMutation'

import type {
  ClipboardHandlers,
  ClipboardPayloadEntry,
} from 'modules/shift_management/components/ShiftManagement/TimeGridTable/types'
import type { ShiftsRow } from 'modules/shift_management/lib/types'
import type {
  GridClipboardPayload,
  ExtendedGridEventAppearance,
} from 'modules/shift_management/utils/TimeGridTable/transformEvents'
import {
  ShiftManagementAddRestDaysDocument,
  ShiftManagementBulkDeleteShiftsDocument,
  ShiftManagementBulkDeleteRestDaysDocument,
  ShiftManagementBulkDropDocument,
  ShiftsShiftFragmentDoc,
  ShiftsRestDayFragmentDoc,
} from 'generated/resources/graphql'
import { groupBy } from 'lodash'
import { useMutation } from 'modules/core/hooks/useMutation'
import { i18nDateToString } from 'modules/core/lib/dates'
import { useShiftsRows } from 'modules/shift_management/hooks/useShiftsRows'
import { useRefreshStats } from 'modules/shift_management/hooks/refreshShiftsRows'
import RestDay from 'modules/shift_management/store/internal/models/RestDay'
import Shift from 'modules/shift_management/store/internal/models/Shift'

type ShiftRef = { _ref: string }

interface UseClipboardProps {
  canUseClipboardActions: boolean
  shiftsRows: ShiftsRow[]
}

type ShiftPasteItem = {
  sourceShifts: Shift[]
  targetRowId: string
  targetDate: string
  mode: 'copy' | 'move'
  dropOverrides?: {
    startTime?: string | null
    locationId?: number | null
    workAreaId?: number | null
  }
}

type RestDayPasteItem = {
  sourceRestDays: RestDay[]
  targetRowId: string
  targetDate: string
  mode: 'copy' | 'move'
}

type TargetDeleteItem = {
  targetShiftsToDelete: Shift[]
  targetRestDaysToDelete: RestDay[]
}

type ShiftPayload = Extract<GridClipboardPayload, { kind: 'shift' }>
type RestDayPayload = Extract<GridClipboardPayload, { kind: 'rest_day' }>
type ShiftGridEvent = Extract<ExtendedGridEventAppearance, { type: 'shift' }>
type RestDayGridEvent = Extract<ExtendedGridEventAppearance, { type: 'rest_day' }>

const isShiftPayload = (payload: GridClipboardPayload): payload is ShiftPayload =>
  payload.kind === 'shift'
const isRestDayPayload = (payload: GridClipboardPayload): payload is RestDayPayload =>
  payload.kind === 'rest_day'
const isShiftGridEvent = (event: ExtendedGridEventAppearance): event is ShiftGridEvent =>
  event.type === 'shift'
const isRestDayGridEvent = (event: ExtendedGridEventAppearance): event is RestDayGridEvent =>
  event.type === 'rest_day'

const payloadIdentityKey = (payload: GridClipboardPayload): string => {
  if (payload.kind === 'shift') return `shift:${payload.shift.id}`
  return `rest_day:${payload.restDay.id}`
}

const useGridClipboardActions = ({
  canUseClipboardActions,
  shiftsRows,
}: UseClipboardProps):
  | ClipboardHandlers<{ id: string }, ExtendedGridEventAppearance>
  | undefined => {
  const { company } = useCurrentSession()
  const [{ shiftsRows: currentShiftsRows }] = useShiftsRows()
  const { refreshStats } = useRefreshStats()
  const [bulkDropShifts] = useMutation(ShiftManagementBulkDropDocument, {
    extractorFn: (data) => data.shiftManagementMutations.bulkDropShiftmanagementShift,
    update: (cache, { data }, options) => {
      const droppedShifts = data?.shiftManagementMutations.bulkDropShiftmanagementShift.shifts
      if (!droppedShifts || droppedShifts.length === 0) return

      const removeShiftIdsFromEntity = ({
        typename,
        entityId,
        idsToRemove,
      }: {
        typename: 'ShiftManagementEmployeesPermission' | 'ShiftManagementWorkArea'
        entityId: number
        idsToRemove: Set<string>
      }) => {
        if (idsToRemove.size === 0) return

        cache.modify({
          id: cache.identify({ __typename: typename, id: entityId }),
          fields: {
            shifts(existingShifts = [], { readField }) {
              return existingShifts.filter((item: ShiftRef) => {
                const id = readField('id', item)
                return typeof id !== 'string' || !idsToRemove.has(id)
              })
            },
          },
        })
      }

      const appendShiftRefsToEntity = ({
        typename,
        entityId,
        shiftsToAppend,
        refsById,
      }: {
        typename: 'ShiftManagementEmployeesPermission' | 'ShiftManagementWorkArea'
        entityId: number
        shiftsToAppend: Array<{ id: string }>
        refsById: Map<string, unknown>
      }) => {
        if (shiftsToAppend.length === 0) return

        cache.modify({
          id: cache.identify({ __typename: typename, id: entityId }),
          fields: {
            shifts(existingShifts = [], { readField }) {
              const existingIds = new Set<string>(
                existingShifts
                  .map((item: ShiftRef) => readField('id', item))
                  .filter((id: unknown): id is string => typeof id === 'string')
              )

              const refsToAdd = shiftsToAppend.flatMap((shift) => {
                if (existingIds.has(shift.id)) return []

                const shiftRef = refsById.get(shift.id)
                if (!shiftRef) return []

                existingIds.add(shift.id)
                return [shiftRef]
              })

              return refsToAdd.length > 0 ? [...existingShifts, ...refsToAdd] : existingShifts
            },
          },
        })
      }

      if (
        typeof options.context === 'object' &&
        options.context !== null &&
        'previousShifts' in options.context
      ) {
        const previousShiftsRaw = options.context.previousShifts
        const previousShifts = Array.isArray(previousShiftsRaw) ? previousShiftsRaw : []

        const previousShiftsByEmployee = groupBy(previousShifts, 'employeeId')
        Object.entries(previousShiftsByEmployee).forEach(([employeeId, employeePreviousShifts]) => {
          const idsToRemove = new Set(employeePreviousShifts.map((s: { id: string }) => s.id))
          removeShiftIdsFromEntity({
            typename: 'ShiftManagementEmployeesPermission',
            entityId: Number(employeeId),
            idsToRemove,
          })
        })

        const previousShiftsByWorkArea = groupBy(
          previousShifts.filter(
            (shift: { workAreaId?: number | null }) =>
              typeof shift.workAreaId === 'number' && shift.workAreaId > 0
          ),
          'workAreaId'
        )

        Object.entries(previousShiftsByWorkArea).forEach(([workAreaId, shiftsInWorkArea]) => {
          const idsToRemove = new Set(shiftsInWorkArea.map((s: { id: string }) => s.id))

          removeShiftIdsFromEntity({
            typename: 'ShiftManagementWorkArea',
            entityId: Number(workAreaId),
            idsToRemove,
          })
        })
      }

      const droppedShiftRefsById = new Map<string, unknown>()
      droppedShifts.forEach((droppedShift) => {
        const newRef = cache.writeFragment({
          data: droppedShift,
          fragment: ShiftsShiftFragmentDoc,
          fragmentName: 'ShiftsShift',
        })
        if (newRef) droppedShiftRefsById.set(droppedShift.id, newRef)
      })

      const droppedShiftsByEmployee = groupBy(droppedShifts, 'employeeId')
      Object.entries(droppedShiftsByEmployee).forEach(([employeeId, employeeDroppedShifts]) => {
        appendShiftRefsToEntity({
          typename: 'ShiftManagementEmployeesPermission',
          entityId: Number(employeeId),
          shiftsToAppend: employeeDroppedShifts,
          refsById: droppedShiftRefsById,
        })
      })

      const droppedShiftsWithWorkArea = droppedShifts.filter(
        (droppedShift) =>
          typeof droppedShift.locationsWorkAreaId === 'number' &&
          droppedShift.locationsWorkAreaId > 0
      )
      const droppedShiftsByWorkArea = groupBy(droppedShiftsWithWorkArea, 'locationsWorkAreaId')
      Object.entries(droppedShiftsByWorkArea).forEach(([workAreaId, workAreaDroppedShifts]) => {
        appendShiftRefsToEntity({
          typename: 'ShiftManagementWorkArea',
          entityId: Number(workAreaId),
          shiftsToAppend: workAreaDroppedShifts,
          refsById: droppedShiftRefsById,
        })
      })
    },
  })
  const [bulkCreateRestDays] = useMutation(ShiftManagementAddRestDaysDocument, {
    extractorFn: (data) => data.shiftManagementMutations.bulkCreateShiftmanagementRestDay,
    update: (cache, { data }) => {
      const restDays = data?.shiftManagementMutations.bulkCreateShiftmanagementRestDay.restDays
      if (!restDays || restDays.length === 0) return

      const restDaysByEmployee = groupBy(restDays, (restDay) => restDay.employee?.id)
      Object.entries(restDaysByEmployee).forEach(([employeeId, employeeRestDays]) => {
        cache.modify({
          id: cache.identify({
            __typename: 'ShiftManagementEmployeesPermission',
            id: Number(employeeId),
          }),
          fields: {
            restDays(existingRestDays = [], { readField }) {
              const existingIds = new Set<string>(
                existingRestDays.map((item: { _ref: string }) => readField('id', item))
              )

              const newRefs = employeeRestDays
                .filter((s) => !existingIds.has(s.id))
                .map((restDay) =>
                  cache.writeFragment({
                    data: restDay,
                    fragment: ShiftsRestDayFragmentDoc,
                    fragmentName: 'ShiftsRestDay',
                  })
                )

              return [...existingRestDays, ...newRefs]
            },
          },
        })
      })
    },
  })
  const [bulkDeleteRestDays] = useMutation(ShiftManagementBulkDeleteRestDaysDocument, {
    extractorFn: (data) => data.shiftManagementMutations.bulkDeleteShiftmanagementRestDay,
    update: (cache, { data }) => {
      data?.shiftManagementMutations.bulkDeleteShiftmanagementRestDay.restDays?.forEach(
        (restDay) => {
          cache.evict({
            id: cache.identify({
              __typename: 'ShiftManagementRestDay',
              id: restDay.id,
            }),
          })
        }
      )
    },
  })
  const [bulkDeleteShifts] = useMutation(ShiftManagementBulkDeleteShiftsDocument, {
    extractorFn: (data) => data.shiftManagementMutations.bulkDeleteShiftmanagementShift,
    update: (cache, { data }) => {
      data?.shiftManagementMutations.bulkDeleteShiftmanagementShift.shifts?.forEach((shift) => {
        cache.evict({
          id: cache.identify({
            __typename: 'ShiftManagementShift',
            id: shift.id,
          }),
        })
      })
    },
  })
  return useUnsafeMemo(() => {
    if (!canUseClipboardActions) return undefined

    const shiftsRowsByKey = new Map(shiftsRows.map((row) => [row.key, row]))
    const extractSourceData = (payload: GridClipboardPayload[]) => {
      const sourceShifts = payload.filter(isShiftPayload).map((item) => item.shift)
      const sourceRestDays = payload.filter(isRestDayPayload).map((item) => item.restDay)

      return {
        sourceShifts,
        sourceShiftIds: new Set(sourceShifts.map((shift) => shift.id)),
        sourceRestDays,
        sourceRestDayIds: new Set(sourceRestDays.map((restDay) => restDay.id)),
      }
    }
    const getTargetItemsToReplace = ({
      targetEvents,
      sourceShiftIds,
      sourceRestDayIds,
    }: {
      targetEvents: ExtendedGridEventAppearance[]
      sourceShiftIds: Set<string>
      sourceRestDayIds: Set<string>
    }) => {
      // Keep items already present in the source payload to avoid deleting the same
      // entity when source/target overlap (e.g. self-paste or move to overlapping cell).
      const targetShiftsToDelete = targetEvents
        .filter(isShiftGridEvent)
        .map((event) => event.shift)
        .filter((shift) => !sourceShiftIds.has(shift.id))

      const targetRestDaysToDelete = targetEvents
        .filter(isRestDayGridEvent)
        .map((event) => event.restDay)
        .filter((restDay) => !sourceRestDayIds.has(restDay.id))

      return { targetShiftsToDelete, targetRestDaysToDelete }
    }
    const deleteTargetItemsToReplace = async ({
      targetShiftsToDelete,
      targetRestDaysToDelete,
    }: {
      targetShiftsToDelete: Shift[]
      targetRestDaysToDelete: RestDay[]
    }) => {
      if (targetShiftsToDelete.length > 0) {
        const destroyTargetShiftsResult = await bulkDeleteShifts({
          variables: {
            ids: targetShiftsToDelete.map((shift) => shift.id),
            destroyBackupShifts: true,
            destroyAutomaticShifts: false,
          },
        })
        const destroyedTargetShiftsHandled = handleResult(
          destroyTargetShiftsResult,
          (_errors) => {}
        )
        if (!destroyedTargetShiftsHandled.success) return false
      }

      if (targetRestDaysToDelete.length > 0) {
        const destroyTargetRestDaysResult = await bulkDeleteRestDays({
          variables: { ids: targetRestDaysToDelete.map((restDay) => restDay.id) },
        })
        const destroyedTargetRestDaysHandled = handleResult(
          destroyTargetRestDaysResult,
          (_errors) => {}
        )
        if (!destroyedTargetRestDaysHandled.success) return false
      }

      return true
    }
    const pasteShiftsBatch = async ({
      items,
      targetDeleteItems,
    }: {
      items: ShiftPasteItem[]
      targetDeleteItems: TargetDeleteItem[]
    }) => {
      const validItems = items.filter(({ sourceShifts }) => sourceShifts.length > 0)
      if (validItems.length === 0) {
        for (const targetDeleteItem of targetDeleteItems) {
          const targetCleared = await deleteTargetItemsToReplace(targetDeleteItem)
          if (!targetCleared) return false
        }

        return true
      }

      const result = await bulkDropShifts({
        variables: {
          items: validItems.map(
            ({ sourceShifts, targetRowId, targetDate, mode, dropOverrides }) => ({
              shiftIds: sourceShifts.map((shift) => shift.id),
              employeeIds: [targetRowId],
              companyId: company.id,
              date: targetDate,
              dropAction: mode,
              startTime: dropOverrides?.startTime ?? null,
              locationId: dropOverrides?.locationId ? String(dropOverrides.locationId) : null,
              workAreaId: dropOverrides?.workAreaId ? String(dropOverrides.workAreaId) : null,
            })
          ),
        },
        context: {
          previousShifts: validItems
            .filter(({ mode }) => mode === 'move')
            .flatMap(({ sourceShifts }) => sourceShifts),
        },
      })

      const handled = handleResult(result, (_errors) => {})
      if (!handled.success) return false

      for (const targetDeleteItem of targetDeleteItems) {
        const targetCleared = await deleteTargetItemsToReplace(targetDeleteItem)
        if (!targetCleared) return false
      }

      const droppedShifts = handled.data.shifts
      if (!droppedShifts || droppedShifts.length === 0) return true

      return true
    }
    const pasteRestDaysToTarget = async ({
      sourceRestDays,
      targetRowId,
      targetDate,
      mode,
    }: RestDayPasteItem) => {
      if (sourceRestDays.length === 0) return true

      const createResult = await bulkCreateRestDays({
        variables: {
          restDays: sourceRestDays.map(() => ({
            employeeId: targetRowId,
            date: targetDate,
            companyId: company.id,
          })),
        },
      })

      const createdHandled = handleResult(createResult, (_errors) => {})
      if (!createdHandled.success) return false

      createdHandled.data?.restDays?.forEach((restDay) => {
        RestDay.build({
          id: restDay.id,
          employee_id: restDay.employee?.id || '',
          date: restDay.date,
          state: restDay.state,
          timezone: restDay.timezone || '',
          employeeFullName: restDay.employee?.fullName || '',
          employeeAvatar: restDay.employee?.avatar?.url || '',
        })
      })

      if (mode === 'move') {
        const destroyResult = await bulkDeleteRestDays({
          variables: { ids: sourceRestDays.map((restDay) => restDay.id) },
        })
        const destroyedHandled = handleResult(destroyResult, (_errors) => {})
        if (!destroyedHandled.success) return false
      }

      return true
    }

    return {
      getCopyPayload: (context) => {
        const seenPayloads = new Set<string>()
        const payloadEntries = context.cells.map<ClipboardPayloadEntry>((selectionCell) => {
          const payload = selectionCell.context.events
            .flatMap((event) => (event.clipboardPayload ? [event.clipboardPayload] : []))
            .filter((clipboardPayload) => {
              const identity = payloadIdentityKey(clipboardPayload)
              if (seenPayloads.has(identity)) return false
              seenPayloads.add(identity)
              return true
            })

          return {
            rowOffset: selectionCell.rowOffset,
            columnOffset: selectionCell.columnOffset,
            payload,
          }
        })

        // Return null only when the entire selection has no content at all
        if (payloadEntries.every((entry) => entry.payload.length === 0)) return null

        return payloadEntries
      },
      canPaste: () => {
        // will be false if disabled cell or other conditions TBD
        return true
      },
      onPaste: async (context) => {
        const shouldReplaceTargetContent = context.replaceTargetContent !== false
        const targetCellsByOffset = new Map(
          context.target.cells.map((selectionCell) => [
            `${selectionCell.rowOffset}:${selectionCell.columnOffset}`,
            selectionCell.context,
          ])
        )
        const payloadByTargetCell = new Map<
          string,
          {
            targetCell: (typeof context.target.cells)[number]['context']
            payload: GridClipboardPayload[]
          }
        >()

        context.payload.forEach((payloadEntry) => {
          const key = `${payloadEntry.rowOffset}:${payloadEntry.columnOffset}`
          const targetCell = targetCellsByOffset.get(key)
          if (!targetCell) return

          const current = payloadByTargetCell.get(key)
          if (!current) {
            payloadByTargetCell.set(key, { targetCell, payload: payloadEntry.payload })
            return
          }

          current.payload = [...current.payload, ...payloadEntry.payload]
        })

        const shiftPasteItems: ShiftPasteItem[] = []
        const restDayPasteItems: RestDayPasteItem[] = []
        const targetDeleteItems: TargetDeleteItem[] = []

        for (const { targetCell, payload } of payloadByTargetCell.values()) {
          const targetRow = shiftsRowsByKey.get(targetCell.entity.id)
          if (!targetRow) continue

          const { sourceShifts, sourceRestDays, sourceShiftIds, sourceRestDayIds } =
            extractSourceData(payload)
          const { targetShiftsToDelete, targetRestDaysToDelete } = getTargetItemsToReplace({
            targetEvents: targetCell.events,
            sourceShiftIds,
            sourceRestDayIds,
          })
          const targetDate = i18nDateToString(targetCell.slot.start, 'backend')
          const targetKey = `${targetCell.entity.id}:${targetCell.slot.index}`
          const dropOverrides = context.dropOverridesByOffset?.[targetKey]
          if (shouldReplaceTargetContent) {
            targetDeleteItems.push({
              targetShiftsToDelete,
              targetRestDaysToDelete,
            })
          }

          if (sourceShifts.length > 0) {
            shiftPasteItems.push({
              sourceShifts,
              targetRowId: targetRow.id,
              targetDate,
              mode: context.mode,
              dropOverrides,
            })
          }

          const restDaysPasted = await pasteRestDaysToTarget({
            sourceRestDays,
            targetRowId: targetRow.id,
            targetDate,
            mode: context.mode,
          })
          if (!restDaysPasted) return
        }

        const shiftsPasted = await pasteShiftsBatch({
          items: shiftPasteItems,
          targetDeleteItems,
        })
        if (!shiftsPasted) return

        if (payloadByTargetCell.size === 0) {
          return
        }

        refreshStats(currentShiftsRows)
      },
    }
  }, [
    canUseClipboardActions,
    company?.id,
    bulkDropShifts,
    bulkDeleteShifts,
    bulkCreateRestDays,
    bulkDeleteRestDays,
    shiftsRows,
    currentShiftsRows,
    refreshStats,
  ])
}

export { useGridClipboardActions }
